// Native
import { Link } from "react-router-dom"

// Components
import { Accordion, AccordionButton, AccordionIcon, AccordionItem, AccordionPanel, Badge, Button, ButtonGroup, HStack, ModalBody, ModalFooter, Stack, Text, useDisclosure } from "@chakra-ui/react"
import { Alert, Clipboard, Dev, Icon, IconButton, Label, Modal, Profile, Show } from "components"

// Config
import { Enums, Moment, Mask } from "config"

// Hooks
import { useWithdraw } from "hooks"

// Screens
import { Loading, Withdraw } from "screens"

export default function View({ data, ...props }){
    
    const withdraw = useWithdraw(data?.id)
    const approve = useDisclosure()
    const reject = useDisclosure()
    const send = useDisclosure()
    const fiscal = useDisclosure()
    const accounts = useDisclosure()

    if(!!withdraw.isLoading) return <Loading isOpen={props.isOpen} text="Carregando pedido de saque..." />

    return(
    <>
    <Modal size="lg" title={`Pedido de saque #${withdraw.data?.cod}`} {...props}>
        <ModalBody>

            <Show visible={withdraw.data?.value >= 1930 * 100}><Alert mb={4}>A nota fiscal é necessária pois o pedido de saque ultrapassa o valor de R$ 1.930,00.</Alert></Show>

            <Accordion>

                {/* Dados da solicitação */}
                <AccordionItem >
                    <AccordionButton>
                        <Text fontSize="sm" children="Dados da solicitação" />
                        <Badge mx={2} colorScheme={Enums.WithdrawStatus.data(withdraw.data?.status).colorScheme} children={withdraw.data?.status_label} />
                        <AccordionIcon />
                    </AccordionButton>
                    <AccordionPanel>
                        <Stack>
                            <Label label="Solicitante:" children={<Link to={`/consultants/${withdraw.data?.consultant?.id}`} children={withdraw.data?.consultant?.customer?.name} />} />
                            <HStack spacing={8}>
                                <Label label="Valor:" fontSize="lg" fontWeight="bold" children={`R$ ${Mask.price(withdraw.data?.value)}`} />
                                <Label label="Taxa:" children={`R$ ${Mask.price(withdraw.data?.tax)}`} />
                            </HStack>
                            <Label label="Data da solicitação:" fontSize="sm" children={Moment(withdraw.data?.created_at).format('LLL')} />
                        </Stack>
                    </AccordionPanel>
                </AccordionItem>

                {/* Conta Bancária */}
                <AccordionItem>
                    <AccordionButton>
                        <Text fontSize="sm" children="Dados bancários" />
                        <AccordionIcon />
                    </AccordionButton>
                    <AccordionPanel>
                        <Stack spacing={3}>
                            { withdraw.data?.bank?.name && <Label label="Titular:" textTransform="capitalize" children={withdraw.data?.bank?.name} /> }
                            { withdraw.data?.bank?.agency && <Label label="Agência:" children={withdraw.data?.bank?.agency} /> }
                            { withdraw.data?.bank?.account && <Label label="Conta:" children={withdraw.data?.bank?.account} /> }
                            { withdraw.data?.bank?.operation && <Label label="Operação:" children={withdraw.data?.bank?.operation} /> }
                            { !!withdraw.data?.bank?.pix_key && <HStack spacing={8}><Label label="Tipo de chave:" children={withdraw.data?.bank?.pix_type_label} /><Label label="Chave Pix:" children={<Clipboard children={withdraw.data?.bank?.pix_key} />} /></HStack> }
                        
                            <Button size="sm" leftIcon={<Icon name="bank" />} onClick={accounts.onOpen}>Visualizar outras contas bancárias</Button>
                        </Stack>
                    </AccordionPanel>
                </AccordionItem>

                {/* Dados do cancelamento */}
                <Show visible={withdraw.data?.status === Enums.WithdrawStatus.REJECTED}>
                    <AccordionItem>
                        <AccordionButton>
                            <Text fontSize="sm" children="Dados do cancelamento" />
                            <AccordionIcon />
                        </AccordionButton>
                        <AccordionPanel>
                            <Stack>
                                <Label label="Rejeitado em" children={Moment(withdraw.data?.rejected_at).format('LLL')} />
                                <Label label="Rejeitado por" children={<Profile name={withdraw.data?.rejected_by?.name} mt={2} />} />
                                <Label label="Motivo da rejeição:" children={withdraw.data?.rejected_reason} fontSize="sm" />
                            </Stack>
                        </AccordionPanel>
                    </AccordionItem>
                </Show>

                {/* Dados da aprovação */}
                <Show visible={withdraw.data?.status === Enums.WithdrawStatus.APPROVED}>
                    <AccordionItem>
                        <AccordionButton>
                            <Text fontSize="sm" children="Dados da aprovação" />
                            <AccordionIcon />
                        </AccordionButton>
                        <AccordionPanel>
                            <Stack>
                                <Label label="Aprovado em" children={Moment(withdraw.data?.approved_at).format('LLL')} />
                                <Label label="Aprovado por" children={<Profile name={withdraw.data?.approved_by?.name ?? 'Sistema'} mt={2} />} />
                            </Stack>
                        </AccordionPanel>
                    </AccordionItem>
                </Show>

                {/* Dados da transferência */}
                <Show visible={withdraw.data?.status === Enums.WithdrawStatus.DONE}>
                    <AccordionItem>
                        <AccordionButton>
                            <Text fontSize="sm" children="Dados da transferência" />
                            <AccordionIcon />
                        </AccordionButton>
                        <AccordionPanel>
                            <Stack>
                                <Label label="Transferência realizada em:" fontSize="sm" children={Moment(withdraw.data?.sent_at).format('LLL')} />
                                <Label label="Enviado por" children={<Profile name={withdraw.data?.sent_by?.name ?? 'Sistema'} mt={2} />} />

                                <ButtonGroup mt={4} size="sm">
                                    <Button children="Download do recibo" leftIcon={<Icon name="download" />} isDisabled={!withdraw.data?.receipt} onClick={() => window.open(withdraw.data?.receipt?.download_url)} />
                                    <Button children="Anexar recibo" leftIcon={<Icon name="attachment" />} colorScheme="orange" isDisabled={!!withdraw.data?.receipt} />
                                </ButtonGroup>
                            </Stack>
                        </AccordionPanel>
                    </AccordionItem>
                </Show>

                {/* Nota Fiscal */}
                <Show visible={withdraw.data?.value >= 1930 * 100}>
                    <AccordionItem>
                        <AccordionButton>
                            <Text fontSize="sm" children="Nota fiscal" />
                            <AccordionIcon />
                        </AccordionButton>
                        <AccordionPanel>
                            <Alert status="error" mb={4}>A nota fiscal ainda não foi anexada.</Alert>
                            <ButtonGroup size="sm">
                                <Button children="Download da Nota Fiscal" leftIcon={<Icon name="download" />} isDisabled={!withdraw.data?.fiscal_note} onClick={() => window.open(withdraw.data?.fiscal_note?.download_url)} />
                                <Button children="Anexar nota" leftIcon={<Icon name="attachment" />} colorScheme="orange" isDisabled={!!withdraw.data?.fiscal_note} onClick={fiscal.onOpen} />
                            </ButtonGroup>
                        </AccordionPanel>
                    </AccordionItem>
                </Show>

            </Accordion>
        </ModalBody>
        <ModalFooter>
            <ButtonGroup size="sm" isDisabled={withdraw.isFetching}>

                <Dev data={withdraw.data} />

                <Show visible={withdraw.data?.status === Enums.WithdrawStatus.ANALYZING}>
                    <Button colorScheme="green" children="Aprovar" onClick={approve.onOpen} leftIcon={<Icon name="check" />} />
                </Show>

                <Show visible={withdraw.data?.status === Enums.WithdrawStatus.APPROVED}>
                    <Button children="Enviar saque" onClick={send.onOpen} leftIcon={<Icon name="check" />} colorScheme="teal" />
                </Show>

                <Show visible={[Enums.WithdrawStatus.ANALYZING, Enums.WithdrawStatus.APPROVED].includes(withdraw.data?.status)}>
                    <Button children="Rejeitar" onClick={reject.onOpen} leftIcon={<Icon name="cancel" />} />
                </Show>

                <IconButton icon="refresh" isLoading={withdraw.isFetching} onClick={withdraw.refetch} />
            </ButtonGroup>
        </ModalFooter>
    </Modal>

    {/* Screens */}
    <Withdraw.Approve data={withdraw.data} {...approve} />
    <Withdraw.Reject data={withdraw.data} {...reject} />
    <Withdraw.Send data={withdraw.data} {...send} />
    <Withdraw.Accounts data={withdraw.data} {...accounts} />
    <Withdraw.Fiscal data={withdraw.data} onSuccess={withdraw.refetch} {...fiscal} />
    </>
    )
}